import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { brokerageAgreementAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import { generateBrokerageAgreementPDF } from '../../utils/pdfGenerator';
import { saveAs } from 'file-saver';
import './BrokerageAgreementList.css';

const BrokerageAgreementList = () => {
  const { t } = useTranslation();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [printingAgreementId, setPrintingAgreementId] = useState(null);

  useEffect(() => {
    loadAgreements();
  }, [page]);

  const loadAgreements = async () => {
    try {
      setLoading(true);
      const response = await brokerageAgreementAPI.getAll(searchTerm, page, pageSize);
      setAgreements(response.data.items || []);
      const totalCount = response.data.totalCount || 0;
      setTotalPages(Math.ceil(totalCount / pageSize) || 1);
    } catch (err) {
      setErrorMessage(t('brokerageAgreement.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadAgreements();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await brokerageAgreementAPI.delete(id);
        setSuccessMessage(t('brokerageAgreement.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadAgreements();
      } catch (err) {
        setErrorMessage(t('brokerageAgreement.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('brokerageAgreement.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('brokerageAgreement.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        await Promise.all(selectedIds.map(id => brokerageAgreementAPI.delete(id)));
        setSuccessMessage(t('brokerageAgreement.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadAgreements();
      } catch (err) {
        setErrorMessage(t('brokerageAgreement.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('brokerageAgreement.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(agreements.map(a => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await brokerageAgreementAPI.exportExcel();
          filename = `BrokerageAgreements_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await brokerageAgreementAPI.exportCsv();
          filename = `BrokerageAgreements_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await brokerageAgreementAPI.exportPdf();
          filename = `BrokerageAgreements_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('brokerageAgreement.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('brokerageAgreement.list.messages.exportError', { format: format.toUpperCase() }));
      setShowErrorModal(true);
    }
  };

  const handlePrint = async (agreement) => {
    try {
      setPrintingAgreementId(agreement.id);
      const response = await brokerageAgreementAPI.getById(agreement.id);
      const agreementDetails = response.data;
      await generateBrokerageAgreementPDF(agreement, agreementDetails);
      setSuccessMessage(t('brokerageAgreement.list.messages.printSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage(t('brokerageAgreement.list.messages.printError'));
      setShowErrorModal(true);
    } finally {
      setPrintingAgreementId(null);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
        <div className="page-actions-left">
          <Link to="/brokerage-agreement/create" className="btn-add">
            + {t('brokerageAgreement.list.addNew')}
          </Link>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder={t('brokerageAgreement.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
            />
            <button className="search-icon" onClick={handleSearch}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
        <div className="action-buttons">
          <div className="export-menu-container">
            <button className="action-btn action-btn-export" title={t('brokerageAgreement.list.buttons.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
              <i className="fas fa-file-export"></i>
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                </button>
                <button onClick={() => handleExport('csv')}>
                  <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                </button>
                <button onClick={() => handleExport('pdf')}>
                  <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                </button>
              </div>
            )}
          </div>
          <button className="action-btn action-btn-delete" title={t('brokerageAgreement.list.buttons.deleteSelected')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={() => setShowSuccessModal(false)}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={confirmData?.title}
        message={confirmData?.message}
        confirmText={confirmData?.confirmText}
        cancelText={confirmData?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length === agreements.length && agreements.length > 0}
                />
              </th>
              <th>{t('brokerageAgreement.list.columns.id')}</th>
              <th>{t('brokerageAgreement.list.columns.property')}</th>
              <th>{t('brokerageAgreement.list.columns.dateFrom')}</th>
              <th>{t('brokerageAgreement.list.columns.dateTo')}</th>
              <th>{t('brokerageAgreement.list.columns.status')}</th>
              <th>{t('brokerageAgreement.list.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {agreements.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  {t('brokerageAgreement.list.messages.noData')}
                </td>
              </tr>
            ) : (
              agreements.map((agreement) => (
                <tr key={agreement.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(agreement.id)}
                      onChange={() => handleSelectOne(agreement.id)}
                    />
                  </td>
                  <td>{agreement.id}</td>
                  <td>
                    {agreement.property ? (
                      <div>
                        <div>{agreement.property.nameEng || agreement.property.NameEng || ''}</div>
                        <small className="text-muted">{agreement.property.code || agreement.property.Code || ''}</small>
                      </div>
                    ) : (
                      t('common.notAvailable')
                    )}
                  </td>
                  <td>{agreement.dateFrom ? new Date(agreement.dateFrom).toLocaleDateString() : t('common.notAvailable')}</td>
                  <td>{agreement.dateTo ? new Date(agreement.dateTo).toLocaleDateString() : t('common.notAvailable')}</td>
                  <td>
                    <span className={agreement.isActive ? 'status-yes-text' : 'status-no-text'}>
                      {agreement.isActive ? t('brokerageAgreement.list.status.active') : t('brokerageAgreement.list.status.inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handlePrint(agreement)}
                        title={t('brokerageAgreement.list.buttons.print')}
                        disabled={printingAgreementId === agreement.id}
                      >
                        {printingAgreementId === agreement.id ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className="fas fa-print"></i>
                        )}
                      </button>
                      <Link
                        to={`/brokerage-agreement/view/${agreement.id}`}
                        className="btn btn-sm btn-view"
                        title={t('brokerageAgreement.list.buttons.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </Link>
                      <Link
                        to={`/brokerage-agreement/edit/${agreement.id}`}
                        className="btn btn-sm btn-edit"
                        title={t('brokerageAgreement.list.buttons.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(agreement.id)}
                        title={t('brokerageAgreement.list.buttons.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                {t('common.previous')}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerageAgreementList;
